import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface Product {
  id: string;
  title: string;
  price: number;
  originalPrice: number;
  image: string;
  seller: string;
  verified: boolean;
  badge: string;
  category: string;
  dispatchHours: number;
  deliveryDays: string;
  warehouse: string;
}

interface ProductQuickViewSheetProps {
  product: Product | null;
  onClose: () => void;
}

export default function ProductQuickViewSheet({ product, onClose }: ProductQuickViewSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    if (!product) return;
    const url = `${window.location.origin}/product/${product.id}`;
    const shareData = {
      title: product.title,
      text: `Check out ${product.title} for $${product.price} on StarBook Prime!`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch {
        // ignore
      }
    }
  };

  useEffect(() => {
    if (product) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [product]);

  if (!product) return null;

  const discount = Math.round(
    ((product.originalPrice - product.price) / product.originalPrice) * 100
  );
  const isSameDay = product.dispatchHours <= 4;
  const isBook = product.category === "Books";

  const badgeColor = () => {
    if (product.badge === "Harvard") return { bg: "#A51C30", color: "#FFD700" };
    if (product.badge === "SAT Prep") return { bg: "#1D4ED8", color: "#fff" };
    if (product.badge === "Cake Art") return { bg: "#EC4899", color: "#fff" };
    if (product.badge === "Summer Hit") return { bg: "#F97316", color: "#fff" };
    return { bg: "linear-gradient(135deg, #B8860B, #DAA520)", color: "#fff" };
  };

  const bc = badgeColor();

  return (
    <>
      {/* Backdrop */}
      <div
        className="lg:hidden fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
        style={{ backdropFilter: "blur(2px)" }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="lg:hidden fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-2xl overflow-hidden"
        style={{
          maxHeight: "85vh",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)",
          animation: "slideUpSheet 0.28s cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header action buttons */}
        <div className="absolute top-3 right-4 flex items-center gap-2">
          {/* Share button */}
          <button
            onClick={handleShare}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all active:scale-90"
            style={{
              background: shareCopied ? "rgba(22,163,74,0.12)" : "rgba(184,134,11,0.1)",
            }}
            title={shareCopied ? "Link copied!" : "Share product"}
          >
            {shareCopied ? (
              <i className="ri-check-line text-sm text-green-600" />
            ) : (
              <i className="ri-share-line text-sm" style={{ color: "#B8860B" }} />
            )}
          </button>
          {/* Close button */}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {/* Share copied toast */}
        {shareCopied && (
          <div
            className="absolute top-14 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
            style={{ background: "#16a34a" }}
          >
            <i className="ri-check-line" />
            Link copied to clipboard!
          </div>
        )}

        <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 40px)" }}>
          {/* Product image */}
          <div className="relative w-full h-56 bg-gray-50 overflow-hidden">
            <img
              src={product.image}
              alt={product.title}
              className="w-full h-full object-cover object-top"
            />
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-1">
              {product.badge && (
                <span
                  className="inline-block px-2 py-0.5 text-xs font-bold rounded"
                  style={{ background: bc.bg, color: bc.color }}
                >
                  {product.badge}
                </span>
              )}
              {discount > 0 && (
                <span className="inline-block px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded">
                  -{discount}% OFF
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-5 pt-4 pb-4">
            {/* Seller */}
            <div className="flex items-center gap-1.5 mb-2">
              {product.verified && (
                <i className="ri-shield-check-fill text-green-500 text-sm" />
              )}
              <span className="text-xs text-gray-400 font-medium">{product.seller}</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-400">{product.warehouse}</span>
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-gray-900 leading-snug mb-3">
              {product.title}
            </h3>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-2xl font-extrabold text-gray-900">${product.price}</span>
              <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
              {discount > 0 && (
                <span className="text-sm font-bold text-red-500">Save {discount}%</span>
              )}
            </div>

            {/* Dispatch info */}
            <div
              className="rounded-xl px-4 py-3 mb-4 flex items-center gap-3"
              style={{
                background: isSameDay
                  ? "rgba(22,163,74,0.08)"
                  : isBook
                  ? "rgba(124,58,237,0.07)"
                  : "rgba(184,134,11,0.08)",
              }}
            >
              <div
                className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{
                  background: isSameDay
                    ? "rgba(22,163,74,0.15)"
                    : isBook
                    ? "rgba(124,58,237,0.12)"
                    : "rgba(184,134,11,0.15)",
                }}
              >
                <i
                  className={`${isBook ? "ri-book-open-line" : "ri-truck-line"} text-lg`}
                  style={{
                    color: isSameDay ? "#16a34a" : isBook ? "#7C3AED" : "#B8860B",
                  }}
                />
              </div>
              <div>
                <p
                  className="text-sm font-bold"
                  style={{
                    color: isSameDay ? "#16a34a" : isBook ? "#7C3AED" : "#B8860B",
                  }}
                >
                  {isSameDay ? "Same-Day Dispatch Available" : isBook ? "Ships Today" : "Fast Dispatch"}
                </p>
                <p className="text-xs text-gray-500">
                  Delivers in {product.deliveryDays} days · {product.warehouse}
                </p>
              </div>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                { icon: "ri-shield-check-line", label: "Buyer Protection" },
                { icon: "ri-secure-payment-line", label: "Secure Payment" },
                { icon: "ri-refresh-line", label: "Easy Returns" },
              ].map((b) => (
                <div
                  key={b.label}
                  className="flex flex-col items-center gap-1 p-2 rounded-lg bg-gray-50 text-center"
                >
                  <i className={`${b.icon} text-base`} style={{ color: "#B8860B" }} />
                  <span className="text-xs text-gray-500 leading-tight">{b.label}</span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col gap-3">
              <Link
                to={`/product/${product.id}`}
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #B8860B, #FFD700)",
                  color: "#1a0f00",
                }}
              >
                <i className="ri-shopping-cart-line" />
                View Full Details &amp; Buy
              </Link>
              <button
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap border border-gray-200 text-gray-600 active:scale-95"
              >
                <i className="ri-arrow-left-line" />
                Continue Browsing
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUpSheet {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
